import { AfterViewInit, Component, effect, inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { features } from '~/core/constants/features';
import { AuthenticationService } from '~/core/services/authentication.service';
import { CreateCardDialogComponent } from '../dialogs/create-card-dialog/create-card-dialog.component';
import { SearchCardDialogComponent } from '../dialogs/search-card-dialog/search-card-dialog.component';
import { StorageService } from '~/core/services/storage.service';
import { CompanyService } from '~/core/services/company.service';
import { OnlineCreditDialogComponent } from '../dialogs/online-credit-dialog/online-credit-dialog.component';
import { UserProfile } from '~/core/constants/user-profile';
import { Company } from '~/core/models/company';
import { NavigationService } from '~/core/services/navigation.service';
import { LocaleService } from '~/core/services/locale.service';
import { DepositWithdrawAgentDialogComponent } from '~/containers/dialogs/deposit-withdraw-agent-dialog/deposit-withdraw-agent-dialog.component';
import { SportbookActions } from '~/core/enum/sportbookActions.enum';
import { CookieService } from '~/core/services/cookie.service';
import { UtilsService } from '~/core/helpers/utils';
import { isLicenseMexican } from '~/core/helpers/license-utils';
import { CompanyStore } from '~/core/store/company.store';
import { AltenarService } from '~/core/services/altenar/altenar.service';
import { UserStore } from '~/core/store/user.store';

declare var sportsbooks: any;

@Component({
  selector: 'app-menu-top-bar',
  template: '',
  imports: [MatProgressSpinnerModule],
})
export class MenuTopBarComponent implements OnInit, AfterViewInit, OnDestroy {
  router = inject(Router);
  auth = inject(AuthenticationService);
  userStore = inject(UserStore);
  dialog = inject(MatDialog);
  translate = inject(TranslateService);
  companyService = inject(CompanyService);
  protected navigation = inject(NavigationService);
  localeService = inject(LocaleService);
  altenarService = inject(AltenarService);
  protected cookiesService? = inject(CookieService);

  protected readonly companyStore: CompanyStore = inject(CompanyStore);

  constructor() {
    effect(() => {
      this.updateUserProperties();
    });
    this.updateCompanyProperties();
    this.companySubscription = this.companyStore.companyObservable().subscribe((company) => {
      this.company = company?.company;
      this.updateCompanyProperties();
    });
  }

  protected loginListener() {
    this.subscriptions.push(
      this.auth.logoutEvent.subscribe(() => {
        this.updateUserProperties();
      }),
    );
  }

  features = features;
  company: Company;
  companySubscription: Subscription;

  companyLogo: string;
  isLoggedIn = false;
  isGuestPlayer = false;

  protected subscriptions: Array<Subscription> = [];

  languageOptions = [
    { id: 'pt-br', name: 'GENERAL.PORTUGUESE', image: 'pt-br', active: false },
    { id: 'en', name: 'GENERAL.ENGLISH', image: 'en', active: false },
    { id: 'es', name: 'GENERAL.SPANISH', image: 'es', active: false },
    { id: 'fr', name: 'GENERAL.FRENCH', image: 'fr', active: false },
    { id: 'es-mx', name: 'GENERAL.SPANISH_MEX', image: 'es-mx', active: false },
  ];
  cardOptions = [
    { id: 'search-card', name: 'GENERAL.CONSULT_CARD', active: false },
    { id: 'create-card', name: 'GENERAL.CREATE_CARD', active: false },
  ];
  depositOptions = [{ id: 'list_deposit', name: 'GENERAL.LIST_DEPOSIT', active: false }];
  actionsOptions = [];
  withdrawOptions = [];

  ngOnInit() {
    this.validateAuthenticatedUser();
    this.changeSelectedLang();
    this.isLoggedIn = this.auth.isLoggedIn();
    this.loginListener();
    if (isLicenseMexican()) {
      this.languageOptions = UtilsService.languageOptionsMexican;
    }
  }

  updateCompanyProperties() {
    this.company = this.companyStore.companySignal();
    this.companyLogo = this.companyService.getLogo();
    this.updateMainMenuOptions();
  }

  ngAfterViewInit() {
    this.updateMenusPermission();
  }

  validateAuthenticatedUser() {
    if (this.auth.isLoggedIn()) {
      this.auth.validateToken();
    }
  }

  openCreateCard() {
    this.dialog.open(CreateCardDialogComponent, { disableClose: false, width: '300px' });
  }

  openSearchCard() {
    this.dialog.open(SearchCardDialogComponent, { disableClose: false, width: '300px' });
  }

  openOnlineCredit() {
    this.dialog.open(OnlineCreditDialogComponent, { disableClose: false, width: '300px' });
  }

  openRules() {
    this.router.navigate(['rules']);
  }

  openSearchPreBet() {
    import('~/containers/dialogs/search-pre-bet-dialog/search-pre-bet-dialog.component').then((c) => {
      this.dialog.open(c.SearchPreBetDialogComponent, { disableClose: false });
    });
  }

  setLanguage(event) {
    const lang = event.id;
    this.translate.use(lang);
    StorageService.currentLanguage = lang;
    this.cookiesService.set('lang', lang);
    this.localeService.setLocale(lang);
    if (window.location.href.includes('sportbook-wireframe') && typeof sportsbooks !== 'undefined') {
      sportsbooks.update({ action: SportbookActions.UPDATE_LANGUAGE, value: lang });
    }
    this.changeSelectedLang();
  }

  isCardEnable(): boolean {
    return this.companyStore.configSignal()?.card?.enable;
  }

  isClientOnline() {
    return AuthenticationService.currentUser?.type === UserProfile.CLIENT;
  }

  isOnlineCreditEnable(): boolean {
    return this.auth.hasPermission(features.ONLINE_CREDITS) && this.companyStore.configSignal()?.online_credit?.enable;
  }

  isPreBetEnabled(): boolean {
    return this.companyStore.configSignal()?.pre_bet?.enable;
  }

  isUserClient() {
    return this.auth.isCurrentUserClient;
  }

  isClientRegister(): boolean {
    return this.companyStore.companySignal()?.client_registration;
  }

  userIsSac() {
    return AuthenticationService.currentUser?.type === UserProfile.SAC;
  }

  isOnlineCreditEnableZone() {
    const zone = AuthenticationService.currentUser?.zone;
    return !zone || zone?.online_credit.enable;
  }

  selectOptionCard(event) {
    const option = event.id;
    if (option === 'create-card') {
      this.openCreateCard();
    } else if (option === 'search-card') {
      this.openSearchCard();
    }
  }

  openRequestAgent(type) {
    const instance = this.dialog.open(DepositWithdrawAgentDialogComponent, { disableClose: false, width: '300px' });
    instance.componentInstance.isDeposit = type === 'deposit';
  }

  selectActions(event) {
    const option = event.id;
    this.openRequestAgent(option);
  }

  updateMenusPermission() {
    const config = this.companyStore.configSignal();
    if (
      AuthenticationService.currentUser?.type === UserProfile.CLIENT &&
      (config?.online_credit?.enable || config?.payments?.deposit?.pix?.enable)
    ) {
      this.depositOptions.push({ id: 'request_deposit', name: 'GENERAL.REQUEST_DEPOSIT', active: false });
      this.depositOptions.reverse();
    }
  }

  changeSelectedLang() {
    let lang = StorageService.currentLanguage;
    if (!lang) {
      lang = this.languageOptions[0].id;
      this.translate.use(lang);
      StorageService.currentLanguage = lang;
      this.cookiesService?.set('lang', lang);
    }

    for (const l of this.languageOptions) {
      l.active = l.id === lang;
    }
  }

  updateUserProperties() {
    this.isLoggedIn = !!this.userStore.userSignal();
    this.isGuestPlayer = this.auth.isGuestPlayer();
    this.updateMainMenuOptions();
  }

  updateMainMenuOptions() {}

  ngOnDestroy(): void {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
  }
}
